<template>
  <div class="w-64 bg-white shadow-md">
     <!-- Mobile backdrop -->
     <transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
        @click="$emit('close')"
      />
    </transition>
    <!-- Sidebar -->
    <aside
      :class="[
        'fixed md:static h-screen w-64 transform transition-transform duration-300',
        'bg-navy-dark text-white z-50 md:z-auto',
        'md:translate-x-0', // Always visible on desktop
        isOpen ? 'translate-x-0' : '-translate-x-full' // Mobile toggle
      ]"
    >
      <!-- Sidebar Header -->
      <div class="p-4 flex justify-between items-center bg-navy-dark">
        <router-link to="/" class="text-xl font-bold text-white">BizMan</router-link>
        <button @click="toggleSidebar" class="md:hidden text-white">
          <LucideX class="w-6 h-6" />
        </button>
      </div>

      <!-- Sidebar Navigation -->
      <nav class="mt-4">
        <ul>
          <!-- Dashboard -->
          <li>
            <router-link to="/" class="flex items-center px-4 py-2 hover:bg-gray-700 space-x-2">
              <LucideLayoutDashboard class="w-5 h-5" />
              <span>Dashboard</span>
            </router-link>
          </li>
          <!-- Accounting (Dropdown) -->
          <li class="relative">
            <button @click="toggleAccountingDropdown" class="w-full flex justify-between px-4 py-2 hover:bg-gray-700">
              <div class="flex items-center space-x-2">
                <LucideDollarSign class="w-5 h-5" />
                <span>Accounts</span>
              </div>
              <LucideChevronDown class="w-5 h-5" />
            </button>
            <ul v-if="isAccountingDropdownOpen" class="bg-gray-800">
              <li>
                <router-link :to="{ name: 'ChartOfAccounts' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideLayers class="w-5 h-5" />
                  <span>Chart of Accounts</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Ledgers' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <BookImage class="w-5 h-5" />
                  <span>Ledgers</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'AccountingTransactions' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideArrowLeftRight class="w-5 h-5" />
                  <span>Transactions</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Invoices' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideFileText class="w-5 h-5" />
                  <span>Invoices</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'AccountsPayable', query: { payable: 'true' } }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideCreditCard class="w-5 h-5" />
                  <span>Accounts Payable</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'AccountsReceivable', query: { receivable: 'true' } }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideCreditCard class="w-5 h-5" />
                  <span>Accounts Receivable</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Expenses' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideCreditCard class="w-5 h-5" />
                  <span>Expenses</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Banks' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBanknote class="w-5 h-5" />
                  <span>Banking</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Cashs' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <BadgeDollarSign class="w-5 h-5" />
                  <span>Cash</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'MFSs' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <Smartphone class="w-5 h-5" />
                  <span>MFS</span>
                </router-link>
              </li>
              <!-- <li>
                <router-link :to="{ name: 'TaxManagement' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideReceipt class="w-5 h-5" />
                  <span>Tax Management</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link :to="{ name: 'Payroll' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideUsers class="w-5 h-5" />
                  <span>Payroll</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link :to="{ name: 'FinancialReports' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBarChart class="w-5 h-5" />
                  <span>Financial Reports</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link to="/accounting/budgets" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideTrendingUp class="w-5 h-5" />
                  <span>Budgets</span>
                </router-link>
              </li> -->
            </ul>
          </li>

          <!-- Purchase (Dropdown) -->
          <li class="relative">
            <button @click="togglePurchaseDropdown" class="w-full flex justify-between px-4 py-2 hover:bg-gray-700">
              <div class="flex items-center space-x-2">
                <LucideShoppingCart class="w-5 h-5" />
                <span>Purchase</span>
              </div>
              <LucideChevronDown class="w-5 h-5" />
            </button>
            <ul v-if="isPurchaseDropdownOpen" class="bg-gray-800">
              <li>
                <router-link :to="{ name: 'Purchases' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideList class="w-5 h-5" />
                  <span>Purchase List</span>
                </router-link>
              </li>
              <li>
                <router-link to="/purchase/payment" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideCreditCard class="w-5 h-5" />
                  <span>Purchase Payment</span>
                </router-link>
              </li>
              <li>
                <router-link to="/purchase/return" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideRotateCw class="w-5 h-5" />
                  <span>Purchase Return</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'PurchaseInvoices', query: { invoiceType: 'purchase' } }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideCreditCard class="w-5 h-5" />
                  <span>Purchase Invoices</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Suppliers' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideUsers class="w-5 h-5" />
                  <span>Suppliers</span>
                </router-link>
              </li>
              <li>
                <router-link to="/purchase/supplier/due"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideAlertTriangle class="w-5 h-5" />
                  <span>Supplier's Due</span>
                </router-link>
              </li>
            </ul>
          </li>

          <!-- Inventory Management (Dropdown) -->
          <li class="relative">
            <button @click="toggleInventoryDropdown" class="w-full flex justify-between px-4 py-2 hover:bg-gray-700">
              <div class="flex items-center space-x-2">
                <LucidePackage class="w-5 h-5" />
                <span>Inventory Management</span>
              </div>
              <LucideChevronDown class="w-5 h-5" />
            </button>
            <ul v-if="isInventoryDropdownOpen" class="bg-gray-800">
              <!-- <li>
                <router-link :to="{ name: 'Products' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Products</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link :to="{ name: 'Variants' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideLayers class="w-5 h-5" />
                  <span>Product Variants</span>
                </router-link>
              </li> -->
              <li>
                <router-link :to="{ name: 'Stocks' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideLayers class="w-5 h-5" />
                  <span>Stocks</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'StockTransactions' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideRefreshCcw class="w-5 h-5" />
                  <span>Transactions</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Categories' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideTag class="w-5 h-5" />
                  <span>Categories</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Units' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideRuler class="w-5 h-5" />
                  <span>Units</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Sizes' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <Landmark class="w-5 h-5" />
                  <span>Sizes</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Colors' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <Smile class="w-5 h-5" />
                  <span>Colors</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Warehouses' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideHome class="w-5 h-5" />
                  <span>Warehouses</span>
                </router-link>
              </li>
              <!-- <li>
                <router-link :to="{ name: 'StockReports' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideClipboardList class="w-5 h-5" />
                  <span>Stock Reports</span>
                </router-link>
              </li> -->
            </ul>
          </li>
          <!-- Sale (Dropdown) -->
          <li class="relative">
            <button @click="toggleSaleDropdown" class="w-full flex justify-between px-4 py-2 hover:bg-gray-700">
              <div class="flex items-center space-x-2">
                <LucideShoppingCart class="w-5 h-5" />
                <span>Sale</span>
              </div>
              <LucideChevronDown class="w-5 h-5" />
            </button>
            <ul v-if="isSaleDropdownOpen" class="bg-gray-800">
              <li>
                <router-link :to="{ name: 'Orders' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideList class="w-5 h-5" />
                  <span>Sale List</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'NewOrder' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <SquarePercent class="w-5 h-5" />
                  <span>New Sale</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'OrderInvoices', query: { invoiceType: 'sale' } }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideCreditCard class="w-5 h-5" />
                  <span>Sale Invoices</span>
                </router-link>
              </li>
              <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideRotateCw class="w-5 h-5" />
                  <span>Sale Return</span>
                </router-link>
              </li>
              <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideUsers class="w-5 h-5" />
                  <span>Customers</span>
                </router-link>
              </li>
              <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideAlertTriangle class="w-5 h-5" />
                  <span>Customer Due / Account Receivable</span>
                </router-link>
              </li>
            </ul>
          </li>
          <li class="relative">
            <button class="w-full flex justify-between px-4 py-2 hover:bg-gray-700">
              <div class="flex items-center space-x-2">
                <router-link :to="{ name: 'NewOrder' }" class="flex items-center py-2 hover:bg-gray-700 space-x-2">
                  <SquarePercent class="w-5 h-5" />
                  <span>POS</span>
                </router-link>
              </div>
            </button>
          </li>
          <!-- Customer Relationship Management (Dropdown) -->
          <li class="relative">
            <button @click="toggleCRMDropdown" class="w-full flex justify-between px-4 py-2 hover:bg-gray-700">
              <div class="flex items-center space-x-2">
                <PersonStanding class="w-5 h-5" />
                <span>CRM</span>
              </div>
              <LucideChevronDown class="w-5 h-5" />
            </button>
            <ul v-if="isCRMDropdownOpen" class="bg-gray-800">
              <!-- <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Dashboard</span>
                </router-link>
              </li> -->
              <li>
                <router-link :to="{ name: 'Customers' }"
                  class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <User class="w-5 h-5" />
                  <span>Customer List</span>
                </router-link>
              </li>
              <!-- <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Leads Management</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Contacts & Accounts</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Deals & Sales Pipeline</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Follow-ups & Reminders</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Quatations & Invoices</span>
                </router-link>
              </li> -->
              <!-- <li>
                <router-link to="#" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideBox class="w-5 h-5" />
                  <span>Customer Support & Tickets</span>
                </router-link>
              </li> -->
            </ul>
          </li>

          <!-- User Management (Dropdown) -->
          <li class="relative">
            <button @click="toggleUserDropdown" class="w-full flex justify-between px-4 py-2 hover:bg-gray-700">
              <div class="flex items-center space-x-2">
                <LucideUsers class="w-5 h-5" />
                <span>User Management</span>
              </div>
              <LucideChevronDown class="w-5 h-5" />
            </button>
            <ul v-if="isUserDropdownOpen" class="bg-gray-800">
              <li>
                <router-link :to="{ name: 'Users' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideList class="w-5 h-5" />
                  <span>Users</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'Roles' }" class="flex items-center px-6 py-2 hover:bg-gray-700 space-x-2">
                  <LucideShield class="w-5 h-5" />
                  <span>Roles</span>
                </router-link>
              </li>
            </ul>
          </li>

          <!-- Settings -->
          <li>
            <router-link to="#" class="flex items-center px-4 py-2 hover:bg-gray-700 space-x-2">
              <LucideSettings class="w-5 h-5" />
              <span>Settings</span>
            </router-link>
          </li>
        </ul>
      </nav>
    </aside>
    <!-- Mobile Sidebar Overlay -->
    <div v-if="isOpen" @click="toggleSidebar" class="fixed inset-0 bg-black opacity-50 md:hidden"></div>
  </div>
</template>

<script>
import { ref } from "vue";
import {
  LucideX,
  LucideLayoutDashboard,
  LucideDollarSign,
  LucideArrowLeftRight,
  LucideFileText,
  LucideCreditCard,
  // LucideBarChart,
  LucideUsers,
  LucideChevronDown,
  LucideList,
  LucideShield,
  LucideSettings,
  LucideLayers,
  LucideBanknote,
  // LucideReceipt,
  // LucideTrendingUp,
  BadgeDollarSign,
  Smartphone,
  LucideShoppingCart,
  LucideRotateCw,
  LucideAlertTriangle,
  BookImage,
  LucidePackage,
  LucideRefreshCcw,
  LucideTag,
  LucideRuler,
  LucideHome,
  // LucideClipboardList,
  Landmark,
  Smile,
  PersonStanding,
  User,
  SquarePercent,
} from "lucide-vue-next";

export default {
  components: {
    LucideX,
    LucideLayoutDashboard,
    LucideDollarSign,
    LucideArrowLeftRight,
    LucideFileText,
    LucideCreditCard,
    // LucideBarChart,
    LucideUsers,
    LucideChevronDown,
    LucideList,
    LucideShield,
    LucideSettings,
    LucideLayers,
    LucideBanknote,
    // LucideReceipt,
    // LucideTrendingUp,
    BadgeDollarSign,
    Smartphone,
    LucideShoppingCart,
    LucideRotateCw,
    LucideAlertTriangle,
    BookImage,
    LucidePackage,
    LucideRefreshCcw,
    LucideTag,
    LucideRuler,
    LucideHome,
    // LucideClipboardList,
    Landmark,
    Smile,
    PersonStanding,
    User,
    SquarePercent,
  },
  props: {
    isOpen: Boolean
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isUserDropdownOpen = ref(false);
    const isAccountingDropdownOpen = ref(false);
    const isPurchaseDropdownOpen = ref(false);
    const isInventoryDropdownOpen = ref(false);
    const isSaleDropdownOpen = ref(false);
    const isCRMDropdownOpen = ref(false);

    const closeAllDropdowns = () => {
      isUserDropdownOpen.value = false;
      isAccountingDropdownOpen.value = false;
      isPurchaseDropdownOpen.value = false;
      isInventoryDropdownOpen.value = false;
      isSaleDropdownOpen.value = false;
      isCRMDropdownOpen.value = false;
    };

    const toggleSidebar = () => {
      emit('close');
    };

    const toggleUserDropdown = () => {
      const wasOpen = isUserDropdownOpen.value;
      closeAllDropdowns();
      isUserDropdownOpen.value = !wasOpen;
    };

    const toggleAccountingDropdown = () => {
      const wasOpen = isAccountingDropdownOpen.value;
      closeAllDropdowns();
      isAccountingDropdownOpen.value = !wasOpen;
    };

    const togglePurchaseDropdown = () => {
      const wasOpen = isPurchaseDropdownOpen.value;
      closeAllDropdowns();
      isPurchaseDropdownOpen.value = !wasOpen;
    };

    const toggleInventoryDropdown = () => {
      const wasOpen = isInventoryDropdownOpen.value;
      closeAllDropdowns();
      isInventoryDropdownOpen.value = !wasOpen;
    };

    const toggleSaleDropdown = () => {
      const wasOpen = isSaleDropdownOpen.value;
      closeAllDropdowns();
      isSaleDropdownOpen.value = !wasOpen;
    };

    const toggleCRMDropdown = () => {
      const wasOpen = isCRMDropdownOpen.value;
      closeAllDropdowns();
      isCRMDropdownOpen.value = !wasOpen;
    };

    return {
      toggleSidebar,
      toggleUserDropdown,
      toggleAccountingDropdown,
      togglePurchaseDropdown,
      toggleInventoryDropdown,
      toggleSaleDropdown,
      toggleCRMDropdown,
      isUserDropdownOpen,
      isAccountingDropdownOpen,
      isPurchaseDropdownOpen,
      isInventoryDropdownOpen,
      isSaleDropdownOpen,
      isCRMDropdownOpen,
    };
  },
  mounted() {
    this.$watch(
      () => this.$route,
      () => {
        if (window.innerWidth < 768) {
          this.$emit('close');
        }
      }
    );
  }
};
</script>
<style scoped>
/* Ensure no white space during transition */


/* Transition effects */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@media (max-width: 767px) {
  .sidebar-mobile {
    top: 0;
    left: 0;
    position: fixed;
    height: 100vh;
    z-index: 50;
  }
}
</style>
